import { useEffect } from 'react';
import { clientFetch } from '~/lib/.client/fetch';
import { setToken } from '~/lib/stores/token';
import type { APITokenResponseBody } from '~/routes/api.token';
import { logger } from '~/utils/logger';

export function useTokenRefresh(loggedIn: boolean) {
  useEffect(() => {
    if (!loggedIn) {
      return undefined;
    }

    let timerId: ReturnType<typeof setTimeout> | undefined;
    let exponentialBackoff = 1_000;

    async function updateToken() {
      try {
        const response = await clientFetch('/api/token');

        if (!response.ok) {
          logger.warn('Failed to obtain token');

          throw new Error();
        }

        const { token, expiresAt } = await response.json<APITokenResponseBody>();

        if (token) {
          setToken(token);
        }

        // reset the backoff
        exponentialBackoff = 1_000;

        timerId = setTimeout(updateToken, calculateTimeToRefresh(expiresAt));
      } catch {
        // try again with an exponential backoff maxed at 5 minutes
        exponentialBackoff = Math.min(exponentialBackoff * 2, 5 * 60_000);
        timerId = setTimeout(updateToken, exponentialBackoff);
      }
    }

    updateToken();

    return () => {
      clearTimeout(timerId);
    };
  }, [loggedIn]);
}

function calculateTimeToRefresh(expiresAt: number) {
  const difference = Math.max(expiresAt - Date.now(), 0);

  /**
   * On the backend side, the token gets refreshed 10 minutes before it expires. The expiration
   * time that we get on the client is also 10 minutes before it expires. If we were to refresh
   * at the expiration date time, it means that the token on the backend side might not be refreshed
   * just yet.
   *
   * This is a little weird as below we are going 1 min past the expiration date. But in essence, this
   * expiration date is not the real one, the token expires 10 min after this expiration date and gets
   * refreshed at this expiration date. So we have to go past it.
   *
   * To be safe and avoid sending too many requests to the backend we do some sanitization:
   *
   *  - We never have that refresh be less than 10 seconds
   *  - We make sure it never go above the limit of 2147483647 (so 1day max)
   *
   * @todo: Move this time addition to the backend side and make it more explicit.
   */
  const oneDay = 24 * 60 * 60_000;

  return Math.min(Math.max(difference - 60_000, 10_000), oneDay);
}
